import { ChatBubble } from 'app/design/icons/chat-bubble';
import { Row } from 'app/design/primitives/box';
import { Text } from 'app/design/primitives/text';

import type { Message, ReplyTo } from 'app/types/chat';
import { MessageKind } from 'app/types/chat';
import React from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { i18n } from 'app/localization/i18n';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconPencil, IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

interface ReplyPreviewProps {
  message: Message;
  onDismiss: () => void;
}

function getMessagePreviewText(message: Message): string {
  if (message.type === MessageKind.File) {
    return i18n.t('one-channel:menu.photo');
  }
  if (typeof message.message === 'string') {
    return message.message;
  }
  // i18n message object – resolve through the translator
  return i18n.t(message.message.i18nKey, { ns: message.message.ns, ...message.message.values });
}

export function ReplyPreview({ message, onDismiss }: ReplyPreviewProps) {
  const { t } = useTranslation('one-channel');
  const authorName = message.user?.firstname;
  const previewText = getMessagePreviewText(message);

  return (
    <View style={styles.container}>
      <View style={styles.accentBar} />
      <Row flex={1} alignItems="center" gap="spacing.general.x2" style={styles.content}>
        <ChatBubble color={theme.colors['text.tertiary']} width={16} height={16} />
        <View style={styles.textContainer}>
          <Text color="text.tertiary" numberOfLines={1}>
            {t('replyingTo', { name: authorName })}
          </Text>
          <Text variant="bodyBase" color="text.secondary" numberOfLines={1} fontSize={14}>
            {previewText}
          </Text>
        </View>
        <Pressable onPress={onDismiss} hitSlop={8} style={styles.closeButton}>
          <IconX strokeWidth={ICON_STROKE_WIDTH} size={18} color={theme.colors['text.secondary']} />
        </Pressable>
      </Row>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    backgroundColor: theme.colors['surface.tertiary-neutral'],
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: theme.colors['surface.disabled'],
    borderLeftColor: theme.colors['surface.disabled'],
    borderLeftWidth: StyleSheet.hairlineWidth,
  },
  accentBar: {
    width: 3,
    backgroundColor: theme.colors['text.tertiary'],
  },
  content: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  textContainer: {
    flex: 1,
    gap: 2,
  },
  closeButton: {
    padding: 4,
  },
});

interface EditPreviewProps {
  message: Message;
  onDismiss: () => void;
}

export function EditPreview({ message, onDismiss }: EditPreviewProps) {
  const { t } = useTranslation('one-channel');
  const previewText = getMessagePreviewText(message);

  return (
    <View style={styles.container}>
      <View style={editStyles.accentBar} />
      <Row flex={1} alignItems="center" gap="spacing.general.x2" style={styles.content}>
        <IconPencil strokeWidth={ICON_STROKE_WIDTH} size={16} color={theme.colors['text.primary']} />
        <View style={styles.textContainer}>
          <Text color="text.primary" numberOfLines={1}>
            {t('editing')}
          </Text>
          <Text variant="bodyBase" color="text.secondary" numberOfLines={1} fontSize={14}>
            {previewText}
          </Text>
        </View>
        <Pressable onPress={onDismiss} hitSlop={8} style={styles.closeButton}>
          <IconX strokeWidth={ICON_STROKE_WIDTH} size={18} color={theme.colors['text.secondary']} />
        </Pressable>
      </Row>
    </View>
  );
}

const editStyles = StyleSheet.create({
  accentBar: {
    width: 3,
    backgroundColor: theme.colors['text.primary'],
  },
});

interface ReplyQuoteProps {
  replyTo: ReplyTo;
}

function getReplyToPreviewText(replyTo: ReplyTo): string {
  if (replyTo.type === MessageKind.File) {
    return i18n.t('one-channel:menu.photo');
  }
  if (!replyTo.message) return '';
  if (typeof replyTo.message === 'string') return replyTo.message;
  return i18n.t(replyTo.message.i18nKey, { ns: replyTo.message.ns, ...replyTo.message.values });
}

export function ReplyQuote({
  replyTo,
  width,
  onPress,
  alignSelf,
}: ReplyQuoteProps & { width: number; onPress?: () => void; alignSelf?: 'flex-start' | 'flex-end' }) {
  if (replyTo.is_deleted) {
    return (
      <View style={[quoteStyles.container, { maxWidth: '60%', minWidth: Math.max(width, 100), alignSelf }]}>
        <View style={quoteStyles.accentBar} />
        <View style={quoteStyles.content}>
          <Text color="text.tertiary" fontSize={12} numberOfLines={1}>
            {replyTo.user?.firstname}
          </Text>
          <Text variant="bodyBase" color="text.secondary" fontSize={12} numberOfLines={1}>
            {i18n.t('one-channel:deletedMessage.other')}
          </Text>
        </View>
      </View>
    );
  }
  const previewText = getReplyToPreviewText(replyTo);

  return (
    <Pressable onPress={onPress} disabled={!onPress}>
      <View style={[quoteStyles.container, { maxWidth: '60%', minWidth: Math.max(width, 100), alignSelf }]}>
        <View style={quoteStyles.accentBar} />
        <View style={quoteStyles.content}>
          <Text color="text.tertiary" fontSize={12} numberOfLines={1}>
            {replyTo.user?.firstname}
          </Text>
          <Text variant="bodyBase" color="text.secondary" fontSize={12} numberOfLines={1}>
            {previewText}
          </Text>
        </View>
      </View>
    </Pressable>
  );
}

const quoteStyles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    borderRadius: 6,
    overflow: 'hidden',
    marginBottom: 4,
    top: theme.spacing['spacing.general.x3'],
  },
  accentBar: {
    width: 3,
    paddingVertical: theme.spacing['spacing.general.x6'],
    backgroundColor: theme.colors['text.tertiary'],
  },
  content: {
    flex: 1,
    paddingHorizontal: 8,
    paddingVertical: 4,
    gap: 1,
  },
});
